import { isEmpty } from 'lodash-es';
import { QueryPartData } from '../../../advanced-search/filters/base/entities/query-part-data.entity';
import { QueryPartStrategy } from '../../../advanced-search/filters/base/entities/query-part-strategy';
import { ShSelectedFacetValue } from '../../../elements/components/facet/entities/facet.entity';
import {
    createDisplayMode,
    DisplayMode
} from '../../../overviews/generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { parseSortInfo } from '../../../overviews/generic-overview/components/display-settings-bar/helpers/sort-info.helper';
import { FacetSelectionType } from '../../../profile-facets/entities/facet-request.entity';
import {
    dateRangeStringToRangeValue,
    rangeValueToString
} from '../../../saved-queries/helpers/convert-structured-query.helper';
import { IsDateRangeQuery, IsLaxDateRangeQuery } from '../../dates/helpers/date-range-query-helper';
import { LogicalOperand, Operand } from '../entities/operand.entity';
import {
    AdvancedSearch,
    AdvancedSearchGroup,
    AdvancedSearchTerm,
    Bucket,
    Facet,
    RangeBucket,
    RangeValue,
    StructuredQuery
} from '../entities/structured-query.entity';
import { ParamField, UrlQueryParams } from '../entities/url-query-params.entity';

export class UrlQueryParamsConverter {
    private static readonly FIELD_VALUE_SEPARATOR: string = '=';
    private static readonly AND_SEPARATOR: string = ',';
    private static readonly OR_SEPARATOR: string = '~';
    private static readonly INCLUDE_OPERATOR: string = '+';
    private static readonly EXCLUDE_OPERATOR: string = '-';

    /*
     *  Regex captures parts preceding unescaped OR separators
     *  Ex: `value\\\~with\\\~tildes~value2` => [`value\\\~with\\\~tildes`,`value2`]
     */
    private static readonly OR_SEPARATOR_REGEX: RegExp = /(?:\\~|[^~])+/g;

    /*
     *  Regex captures parts preceding unescaped AND separators
     *  Ex: `value\\\,with\\\,commas,value2` => [`value\\\,with\\\,commas`,`value2`]
     */
    private static readonly AND_SEPARATOR_REGEX: RegExp = /(?:\\,|[^,])+/g;

    /*
     * Special characters used in url parsing
     * https://documentation.n-able.com/N-central/userguide/Content/Further_Reading/API_Level_Integration/API_Integration_URLEncoding.html
     */
    private static readonly VALUES_TO_ESCAPE: string[] = [
        '\\', // This NEEDS to be the first one to escape
        '$',
        '&',
        '+',
        '-',
        ',',
        '/',
        ':',
        ';',
        '=',
        '?',
        '@',
        ' ',
        '"',
        '<',
        '>',
        '#',
        '%',
        '{',
        '}',
        '|',
        '^',
        '~',
        '[',
        ']',
        '`'
    ];

    // #region Generate QueryParams from StructuredQuery and ShSelectedFacetValues
    public static generateQueryParams(structuredQuery: StructuredQuery): UrlQueryParams {
        const queryParams: UrlQueryParams = {};

        const global = structuredQuery.Global;
        const semantic = structuredQuery.Semantic;
        const fullText = structuredQuery.FullText;
        const advanced = structuredQuery.AdvancedSearch;
        const facets = structuredQuery.Facets;
        const fields = structuredQuery.Fields;
        const columnFilters = structuredQuery.ColumnFilters;

        if (global.length > 0) {
            queryParams[ParamField.Global] = global
                .map(globalValue => this.sanitizeValue(globalValue))
                .join(this.AND_SEPARATOR);
        }

        if (semantic) {
            queryParams[ParamField.Semantic] = this.sanitizeValue(semantic); // SQ can only contain 1 value
        }

        if (fullText) {
            queryParams[ParamField.FullText] = this.sanitizeValue(fullText); // SQ can only contain 1 value
        }

        const advancedParams: string = this.advancedStructuredQueryToQueryParam(advanced);

        if (advancedParams.length > 0) {
            queryParams[ParamField.AdvancedSearch] = advancedParams;
        }

        const facetParams: string = this.facetsToQueryParam(facets);

        if (facetParams.length > 0) {
            queryParams[ParamField.Facets] = facetParams;
        }

        const fieldParams: string = this.queryPartsToQueryParam(fields);

        if (fieldParams.length > 0) {
            queryParams[ParamField.Fields] = fieldParams;
        }

        const columnFilterParams: string = this.queryPartsToQueryParam(columnFilters);

        if (columnFilterParams.length > 0) {
            queryParams[ParamField.ColumnFilter] = columnFilterParams;
        }

        return queryParams;
    }

    public static advancedStructuredQueryToQueryParam(advancedSearch: AdvancedSearch): string {
        const advancedSearchGroupQueries = advancedSearch.Groups.map(advancedSearchGroup =>
            this.advancedSearchGroupToQueryParam(advancedSearchGroup)
        ).filter(query => query !== '');

        const separator: string =
            advancedSearch.LogicalOperand === LogicalOperand.Or ? this.OR_SEPARATOR : this.AND_SEPARATOR;

        return advancedSearchGroupQueries.map(group => `(${group})`).join(separator);
    }

    private static advancedSearchGroupToQueryParam(group: AdvancedSearchGroup): string {
        const terms = group.Terms;

        if (!terms) {
            return '';
        }

        const advancedSearchTermQueries = terms
            .map(term => this.advancedSearchTermToQueryParam(term))
            .filter(query => query !== '');

        if (!advancedSearchTermQueries.length) {
            return '';
        }

        const joinedQueries = advancedSearchTermQueries.join();

        // Only prepend for OR groups
        const logicalOperandString: string = group.LogicalOperand === LogicalOperand.Or ? this.OR_SEPARATOR : '';

        return `${logicalOperandString}${joinedQueries}`;
    }

    private static advancedSearchTermToQueryParam(term: AdvancedSearchTerm): string {
        const operand: string = term.Operand === Operand.Exclude ? this.EXCLUDE_OPERATOR : this.INCLUDE_OPERATOR;

        const sanitizedDottedKey: string = this.sanitizeValue(term.DottedKey);

        const values: string[] | undefined = term.Values;
        const rangeValues: RangeValue[] | undefined = term.RangeValues;
        if (!values?.length) {
            if (!rangeValues?.length) {
                return '';
            }

            const sanitizedValues: string[] = rangeValues.map(unsanitizedValue =>
                this.sanitizeValue(rangeValueToString(unsanitizedValue))
            );
            const valuesQuery: string = sanitizedValues.join(this.AND_SEPARATOR);

            return `${operand}${sanitizedDottedKey}=${valuesQuery}`;
        }

        const sanitizedValues: string[] = values.map(unsanitizedValue => this.sanitizeValue(unsanitizedValue));

        const valuesQuery: string = sanitizedValues.join(this.AND_SEPARATOR);

        return `${operand}${sanitizedDottedKey}=${valuesQuery}`;
    }

    public static facetsToQueryParam(facets: Facet[]): string {
        return facets
            .map(facet => {
                const key: string = facet.DottedKey;
                const sanitizedDottedKey: string = this.sanitizeValue(key);

                let missingValue: string | undefined = undefined;
                // Assuming only include values for now
                let includeValues: string[] = [];

                if (facet.Buckets && facet.Buckets.length > 0) {
                    // For now we only allow excluding of the 'no value' value
                    missingValue = facet.Buckets.find((bucket: Bucket) => bucket.Operand === Operand.Exclude)?.Value;
                    includeValues = facet.Buckets.filter(
                        (bucket: Bucket) => bucket.Operand === Operand.Include
                    ).flatMap((bucket: Bucket) => this.sanitizeValue(bucket.Value));
                } else if (facet.RangeBuckets) {
                    // 'no value' filtering is not supported for range facets.
                    includeValues = facet.RangeBuckets.flatMap((bucket: RangeBucket) =>
                        bucket.RangeValue ? rangeValueToString(bucket.RangeValue) : ''
                    ).map((value: string) => this.sanitizeValue(value));
                }

                const missingQuery: string = missingValue
                    ? `${this.EXCLUDE_OPERATOR}${sanitizedDottedKey}=${this.sanitizeValue(missingValue)}`
                    : '';

                const or: boolean = facet.LogicalOperand === LogicalOperand.Or;

                const operator: string = this.INCLUDE_OPERATOR;
                const separator: string = or ? this.OR_SEPARATOR : this.AND_SEPARATOR;

                // an extra separator is inserted so we can detect if the facet value is
                // of type 'or' when loading the facets back in
                const includingQuery: string =
                    includeValues.length > 0
                        ? `${operator}${sanitizedDottedKey}=${includeValues.join(separator)}${separator}`
                        : '';

                return `${missingQuery}${includingQuery}`;
            })
            .join('');
    }

    public static queryPartsToQueryParam(queryParts: QueryPartData[]): string {
        return queryParts
            .map(queryPart => {
                const exclude: boolean = queryPart.strategy === QueryPartStrategy.MatchNone;
                const key: string = queryPart.field;
                const values: string[] = queryPart.values.map(value => this.sanitizeValue(value));
                const or: boolean = queryPart.strategy === QueryPartStrategy.MatchAtLeastOne;

                const operator: string = exclude ? this.EXCLUDE_OPERATOR : this.INCLUDE_OPERATOR;
                const separator: string = or ? this.OR_SEPARATOR : this.AND_SEPARATOR;

                // an extra separator is inserted so we can detect if the value is
                // of type 'or' when loading the query back in
                return `${operator}${key}=${values.join(separator)}${separator}`;
            })
            .join('');
    }

    public static selectedFacetsToFacetQueryParamValue(selectedFacets: ShSelectedFacetValue[]): string {
        // Optimally we should use same structure as AdvancedSearch query, but for backwards compatibility we use original structure of Facet query params
        const dottedKeys: string[] = [...new Set(selectedFacets.map(facet => facet.key))];

        return dottedKeys
            .map(dottedKey => {
                const sanitizedDottedKey = this.sanitizeValue(dottedKey);
                // Build missing value query
                const missingValue = selectedFacets.find(selected => selected.key === dottedKey && selected.missing);

                const missingQuery: string = missingValue
                    ? `${this.EXCLUDE_OPERATOR}${sanitizedDottedKey}=${this.sanitizeValue(missingValue.value)}`
                    : '';

                // Build include values query
                // Assuming no exclude values except for 'missing' value for now
                const includeFacetValues = selectedFacets.filter(
                    selected => selected.key === dottedKey && !selected.missing
                );
                const includeValues = includeFacetValues.map(selected => this.sanitizeValue(selected.value));
                // All SelectionTypes should be the same anyway
                const orOperation: boolean = includeFacetValues.some(
                    includeFacetValue => includeFacetValue.selectionType === FacetSelectionType.SHOULD
                );

                const operator: string = this.INCLUDE_OPERATOR;
                const separator: string = orOperation ? this.OR_SEPARATOR : this.AND_SEPARATOR;

                // an extra separator is inserted so we can detect if the facet value is
                // of type 'or' when loading the facets back in
                const includingQuery: string =
                    includeValues.length > 0
                        ? `${operator}${sanitizedDottedKey}=${includeValues.join(separator)}${separator}`
                        : '';

                return `${missingQuery}${includingQuery}`;
            })
            .join('');
    }

    // SER-2240 + MED-2575: prevent issues with special characters used in building of query
    private static sanitizeValue(unsanitizedValue: string): string {
        let sanitizedValue: string = unsanitizedValue;

        if (!sanitizedValue) {
            return sanitizedValue;
        }

        // Loop special characters and escape them
        this.VALUES_TO_ESCAPE.forEach(specialChar => {
            sanitizedValue = sanitizedValue.split(specialChar).join(`\\${specialChar}`);
        });

        return sanitizedValue;
    }
    // #endregion

    // #region Generate StructuredQuery from QueryParams

    /**
     * Build StructuredQuery object from the url params
     *
     * @param queryParams UrlQueryParams
     * @returns StructuredQuery
     */
    public static queryParamsToStructuredQuery(queryParams: UrlQueryParams): StructuredQuery {
        const globalSearchValues: string[] = queryParams[ParamField.Global]
            ? this.parseValueString(queryParams[ParamField.Global])[0]
            : [];

        const semanticSearchValue: string = queryParams[ParamField.Semantic]
            ? this.parseValueString(queryParams[ParamField.Semantic])[0][0]
            : '';

        const fullTextSearchValue: string = queryParams[ParamField.FullText]
            ? this.parseValueString(queryParams[ParamField.FullText])[0][0]
            : '';

        const advancedSearch: AdvancedSearch = this.queryParamsToAdvancedSearch(queryParams);

        const facetSearch: Facet[] = queryParams[ParamField.Facets]
            ? this.queryParamToFacets(queryParams[ParamField.Facets])
            : [];

        const fieldSearch: QueryPartData[] = queryParams[ParamField.Fields]
            ? this.queryParamToQueryPartData(queryParams[ParamField.Fields])
            : [];

        const columnFilterSearch: QueryPartData[] = queryParams[ParamField.ColumnFilter]
            ? this.queryParamToQueryPartData(queryParams[ParamField.ColumnFilter])
            : [];

        return {
            Global: globalSearchValues,
            Semantic: semanticSearchValue,
            FullText: fullTextSearchValue,
            AdvancedSearch: advancedSearch,
            Facets: facetSearch,
            Fields: fieldSearch,
            ColumnFilters: columnFilterSearch
        };
    }

    // #region queryParamsToAdvancedSearch
    private static queryParamsToAdvancedSearch(queryParams: UrlQueryParams): AdvancedSearch {
        const advancedSearch = queryParams[ParamField.AdvancedSearch];

        if (!advancedSearch) {
            return {
                LogicalOperand: LogicalOperand.And,
                Groups: []
            };
        }

        const operand: LogicalOperand = this.parseAdvancedSearchLogicalOperand(advancedSearch);

        const groupSeparator: string = operand === LogicalOperand.Or ? this.OR_SEPARATOR : this.AND_SEPARATOR;
        const advancedGroupQueries: string[] = advancedSearch.split(`)${groupSeparator}(`);
        const trimmedAdvancedGroupQueries: string[] = advancedGroupQueries.map(group => {
            let trimmedGroupString: string = group;

            if (trimmedGroupString.startsWith('(')) {
                trimmedGroupString = trimmedGroupString.substring(1);
            }
            if (trimmedGroupString.endsWith(')')) {
                trimmedGroupString = trimmedGroupString.slice(0, -1);
            }
            return trimmedGroupString;
        });

        const advancedGroups: AdvancedSearchGroup[] = trimmedAdvancedGroupQueries.map(query =>
            this.advancedGroupQueryToAdvancedSearchGroup(query)
        );

        return {
            LogicalOperand: operand,
            Groups: advancedGroups
        };
    }

    private static parseAdvancedSearchLogicalOperand(advancedSearchQuery: string): LogicalOperand {
        // Will not be surrounded by () if single group
        const isSingleGroup: boolean = advancedSearchQuery[0] !== '(';

        if (isSingleGroup) {
            // Default to And
            return LogicalOperand.And;
        }

        // Structure of query will be (FieldKey1=value,FieldKey2),(FieldKey3) or (FieldKey1=value,FieldKey2)~(FieldKey3)
        // So search for )~( to determine if OR operand
        // Assume AND operand if not
        return advancedSearchQuery.includes(`)${this.OR_SEPARATOR}(`) ? LogicalOperand.Or : LogicalOperand.And;
    }

    private static advancedGroupQueryToAdvancedSearchGroup(groupQuery: string): AdvancedSearchGroup {
        const firstCharacter: string = groupQuery[0];

        // Legacy queries are not prepended with an operand. Default to AND
        const isOperand: boolean = firstCharacter === this.AND_SEPARATOR || firstCharacter === this.OR_SEPARATOR;
        const groupOperand: LogicalOperand =
            isOperand && firstCharacter === this.OR_SEPARATOR ? LogicalOperand.Or : LogicalOperand.And;

        // Trim operand away
        const trimmedGroupQuery: string = isOperand ? groupQuery.substring(1) : groupQuery;

        const fieldValuePairs: string[] = UrlQueryParamsConverter.splitParamsToFieldValueStrings(trimmedGroupQuery);
        const advancedSearchTerms: AdvancedSearchTerm[] = [];

        fieldValuePairs.forEach(fieldValuePair => {
            // Get operator to determine include/exclude
            const exclude: boolean = fieldValuePair[0] === UrlQueryParamsConverter.EXCLUDE_OPERATOR;
            const fieldValuePairs: string[] = fieldValuePair
                .substring(1)
                .split(UrlQueryParamsConverter.FIELD_VALUE_SEPARATOR);

            if (fieldValuePairs.length !== 2) {
                // in case the url is broken
                return;
            }

            const dottedKey: string = fieldValuePairs[0];
            const desanitizedDottedKey: string = this.desanitizeValue(dottedKey);

            // Split into values
            const [unsanitizedValues, isOrOperation]: [string[], boolean] = this.parseValueString(fieldValuePairs[1]);

            const operand: Operand = this.determineOperand(exclude, isOrOperation);

            const terms: AdvancedSearchTerm[] = unsanitizedValues
                .map(value =>
                    IsDateRangeQuery(value)
                        ? {
                              DottedKey: desanitizedDottedKey,
                              Operand: operand,
                              RangeValues: [dateRangeStringToRangeValue(value)]
                          }
                        : {
                              DottedKey: desanitizedDottedKey,
                              Operand: operand,
                              Values: [value]
                          }
                )
                .reverse();

            advancedSearchTerms.push(...terms);
        });

        // To allow backwards compatibility with old query-params structure.
        // 'Should'/'MatchAtLeastOne' doesn't really exist on Advanced Search.
        // If all of the terms are set to Should, change the Group operand to OR and change the Term operands to Include
        const allShouldTerms: boolean = advancedSearchTerms.every(term => term.Operand === Operand.Should);

        const terms: AdvancedSearchTerm[] = advancedSearchTerms.map(term => ({
            ...term,
            Operand: allShouldTerms ? Operand.Include : term.Operand
        }));

        terms.reverse(); // Reverse so order stays the same
        return {
            LogicalOperand: allShouldTerms ? LogicalOperand.Or : groupOperand,
            Terms: terms
        };
    }
    // #endregion

    // #region queryParamsToSelectedFacetValues
    /**
     * Build array of selected Facet values from the url params.
     *
     * @param queryParams
     * @returns ShSelectedFacetValue[]
     */
    public static queryParamsToSelectedFacetValues(queryParams: UrlQueryParams): ShSelectedFacetValue[] {
        const facets = queryParams[ParamField.Facets];

        if (!facets) {
            return [];
        }

        const facetStrings: string[] = this.splitParamsToFieldValueStrings(facets);

        let selectedFacets: ShSelectedFacetValue[] = [];

        facetStrings.forEach((facetString: string) => {
            const fieldValuePairs: string[] = facetString
                .substring(1)
                .split(UrlQueryParamsConverter.FIELD_VALUE_SEPARATOR); // filter empty values

            if (fieldValuePairs.length < 2) {
                // In case the url is broken
                return;
            }

            const dottedKey: string = fieldValuePairs[0];
            const desanitizedDottedKey: string = this.desanitizeValue(dottedKey);

            const [unsanitizedValues, isOrOperation]: [string[], boolean] = this.parseValueString(fieldValuePairs[1]);

            const isExcludeOperation: boolean = facetString[0] === UrlQueryParamsConverter.EXCLUDE_OPERATOR;

            // If missing value (assuming only possible exclude), search existing selected facets for with same key and reuse selection type
            let selectionType: FacetSelectionType = isOrOperation ? FacetSelectionType.SHOULD : FacetSelectionType.MUST;
            const existingSelectedFacet = selectedFacets.find(facet => facet.key === dottedKey);

            if (isExcludeOperation && existingSelectedFacet) {
                selectionType = existingSelectedFacet.selectionType;
            }
            if (!isExcludeOperation && existingSelectedFacet) {
                existingSelectedFacet.selectionType = selectionType;
            }

            const newFacets: ShSelectedFacetValue[] = unsanitizedValues.map(value => ({
                facetLabel: desanitizedDottedKey,
                key: desanitizedDottedKey,
                valueLabel: value,
                value,
                missing: isExcludeOperation, // Is 'missing' value search if Exclude operation and value '*'
                selectionType
            }));

            selectedFacets = [...selectedFacets, ...newFacets];
        });
        return selectedFacets;
    }

    /**
     * Split params string into field-value strings
     * Ex: +Dynamic.TestField=test-Descriptive.Description=not this => [+Dynamic.TestField=test, -Descriptive.Description=not this]
     *
     * @param params
     */
    private static splitParamsToFieldValueStrings(params: string): string[] {
        const fieldValueStrings: string[] = [];
        let remainingParams: string = params;

        // Split params into field/value groups by iterating over complete string,
        // checking last occurrence of a 'Family' + operator combinations.
        // Then remove end from total string. Repeat until no remaining.
        while (!isEmpty(remainingParams)) {
            const indexLastOperatorAndField: number = this.getLastOperatorAndFamilyOccurrenceIndex(remainingParams);

            const fieldValueString: string = remainingParams.substring(indexLastOperatorAndField);

            // Check and remove operator between fields
            const indexOfPotentialOperator: number =
                remainingParams.indexOf(fieldValueString) - 1 < 0 ? remainingParams.indexOf(fieldValueString) - 1 : 0;
            const potentialOperator: string = remainingParams.charAt(indexOfPotentialOperator);
            const hasOperator: boolean =
                potentialOperator === this.AND_SEPARATOR || potentialOperator === this.OR_SEPARATOR;

            // Split before the separator if there is one
            const splitIndex: number = hasOperator
                ? indexOfPotentialOperator
                : remainingParams.indexOf(fieldValueString);

            remainingParams = remainingParams.substring(0, splitIndex);
            fieldValueStrings.push(fieldValueString);
        }

        return fieldValueStrings;
    }
    // #endregion

    // #region queryParamToQueryPartData
    public static queryParamToQueryPartData(queryParam: string): QueryPartData[] {
        if (!queryParam) {
            return [];
        }

        const fieldStrings: string[] = this.splitParamsToFieldValueStrings(queryParam);

        let queryParts: QueryPartData[] = [];

        fieldStrings.forEach((facetString: string) => {
            const fieldValuePairs: string[] = facetString
                .substring(1)
                .split(UrlQueryParamsConverter.FIELD_VALUE_SEPARATOR); // filter empty values

            if (fieldValuePairs.length < 2) {
                // In case the url is broken
                return;
            }

            const dottedKey: string = fieldValuePairs[0];
            const desanitizedDottedKey: string = this.desanitizeValue(dottedKey);

            const [unsanitizedValues, isOrOperation]: [string[], boolean] = this.parseValueString(fieldValuePairs[1]);

            const isExcludeOperation: boolean = facetString[0] === UrlQueryParamsConverter.EXCLUDE_OPERATOR;

            const newQueryPart: QueryPartData = {
                field: desanitizedDottedKey,
                strategy: this.determineQueryPartStrategy(isExcludeOperation, isOrOperation),
                values: unsanitizedValues
            };

            queryParts = [...queryParts, newQueryPart];
        });
        return queryParts;
    }
    // #endregion

    // #region queryParamToQueryPartData
    public static queryParamToFacets(queryParam: string): Facet[] {
        if (!queryParam) {
            return [];
        }

        const fieldStrings: string[] = this.splitParamsToFieldValueStrings(queryParam);

        let facets: Facet[] = [];

        fieldStrings.forEach((facetString: string) => {
            const fieldValuePairs: string[] = facetString
                .substring(1)
                .split(UrlQueryParamsConverter.FIELD_VALUE_SEPARATOR); // filter empty values

            if (fieldValuePairs.length < 2) {
                // In case the url is broken
                return;
            }

            const dottedKey: string = fieldValuePairs[0];
            const desanitizedDottedKey: string = this.desanitizeValue(dottedKey);

            const [unsanitizedValues, isOrOperation]: [string[], boolean] = this.parseValueString(fieldValuePairs[1]);

            const logicalOperand: LogicalOperand = isOrOperation ? LogicalOperand.Or : LogicalOperand.And;

            const isExcludeOperation: boolean = facetString[0] === UrlQueryParamsConverter.EXCLUDE_OPERATOR;

            const existingFacet: Facet | undefined = facets.find(facet => facet.DottedKey === desanitizedDottedKey);

            const isDate: boolean = IsLaxDateRangeQuery(unsanitizedValues[0]);

            // If date range facet, create RangeBuckets instead of Buckets
            const newBuckets = isDate
                ? []
                : unsanitizedValues.map(value => ({
                      Operand: this.determineOperand(isExcludeOperation, false),
                      Value: isExcludeOperation ? '*' : value
                  }));
            const newRangeBuckets: RangeBucket[] = isDate
                ? unsanitizedValues.map(value => ({
                      Operand: this.determineOperand(isExcludeOperation, false),
                      RangeValue: dateRangeStringToRangeValue(value)
                  }))
                : [];

            if (existingFacet) {
                const existingHasMissingValue: boolean =
                    existingFacet.Buckets?.some(bucket => bucket.Operand === Operand.Exclude) ?? false;

                // If existing facet has missing value, overwrite logical operand
                // If new value is missing value, keep existing logical operand
                const logicalOperandToSet: LogicalOperand = existingHasMissingValue
                    ? logicalOperand
                    : existingFacet.LogicalOperand;

                // Append to existing facet
                existingFacet.LogicalOperand = logicalOperandToSet;
                existingFacet.Buckets = [...(existingFacet.Buckets || []), ...newBuckets];
                existingFacet.RangeBuckets = [...(existingFacet.RangeBuckets || []), ...newRangeBuckets];
                return;
            }

            const newFacet: Facet = {
                LogicalOperand: logicalOperand,
                DottedKey: desanitizedDottedKey,
                Buckets: newBuckets,
                RangeBuckets: newRangeBuckets
            };

            facets = [...facets, newFacet];
        });
        return facets;
    }

    // #endregion

    private static getLastOperatorAndFamilyOccurrenceIndex(remainingParams: string): number {
        const indexLastIncludesOperatorOfFamily: number = this.findLastOperatorAndFieldOccurrenceIndex(
            remainingParams,
            UrlQueryParamsConverter.INCLUDE_OPERATOR
        );
        const indexLastExcludeOperatorOfFamily: number = this.findLastOperatorAndFieldOccurrenceIndex(
            remainingParams,
            UrlQueryParamsConverter.EXCLUDE_OPERATOR
        );

        // Determine the latest of the 2
        return indexLastIncludesOperatorOfFamily > indexLastExcludeOperatorOfFamily
            ? indexLastIncludesOperatorOfFamily
            : indexLastExcludeOperatorOfFamily;
    }

    /**
     * Find the last occurrence of operator + Field combination
     * EX: '-Dynamic.' or '+Dynamic.' or '+RecordStatus'
     * @param inputString
     * @param startingChar
     * @returns
     */
    private static findLastOperatorAndFieldOccurrenceIndex(inputString: string, startingChar: string): number {
        // Pattern starts with provides startingChar, followed by a capital letter, followed by any number of letters
        // MED-3485: Don't use predefined Families for reasoning since new custom families can be added
        // MED-5417 Also include numbers in the regex (e.g. Dynamic.Mh2Albums.Album)
        // MED-5579 Also include underscores in the regex (some customers like their underscores)
        // MED-6567 Also include escaped hyphens and plus signs (e.g. -test\\-field=value)
        // Use negative lookbehind to ensure operator is not preceded by backslash (which would mean it's escaped)
        const regex = new RegExp(`(?<!\\\\)\\${startingChar}[a-zA-Z0-9._\\\\\\-\\+]*=`, 'g');
        let match;
        let lastIndex = -1;

        while ((match = regex.exec(inputString)) !== null) {
            lastIndex = match.index;
        }

        return lastIndex;
    }

    /*
     *   Determine operation type and split into separate unescaped values
     */
    private static parseValueString(valueString: string): [string[], boolean] {
        // Try to split value on OR operator
        // If this generates more than 1 value, we have an OR operation
        // Otherwise we default to an AND operation
        // At this point in time 1 key-value pair can ONLY be either AND or OR operation
        // Mixing these is not supported and should be generated as separate key-values!
        const orOperationValues: string[] = this.splitOrOperationValues(valueString);
        const isOrOperation: boolean =
            orOperationValues.length > 1 ||
            (orOperationValues.length === 1 && `${orOperationValues[0]}~` === valueString); // MED-2727: Single value with trailing OR operator

        const values: string[] = isOrOperation ? orOperationValues : this.splitAndOperationValues(valueString);

        const unsanitizedValues: string[] = values
            .filter(value => !!value) // Filter empty values
            .map(value => this.desanitizeValue(value).trim()); // Unescape special characters

        return [unsanitizedValues, isOrOperation];
    }

    /*
     *  Split value string on AND operator
     */
    private static splitAndOperationValues(valueString: string): string[] {
        return valueString.match(this.AND_SEPARATOR_REGEX) ?? [valueString];
    }

    /*
     *  Split value string on OR operator
     */
    private static splitOrOperationValues(valueString: string): string[] {
        return valueString.match(this.OR_SEPARATOR_REGEX) ?? [valueString];
    }

    // SER-2240 + MED-2575: prevent issues with special characters used in building of query
    private static desanitizeValue(sanitizedValue: string): string {
        let unsanitizedValue: string = sanitizedValue;

        // Loop special characters and unescape them
        this.VALUES_TO_ESCAPE.forEach(
            specialChar => (unsanitizedValue = unsanitizedValue.split(`\\${specialChar}`).join(specialChar))
        );

        return unsanitizedValue;
    }
    // #endregion

    // #region Page settings
    public static queryParamsToSortInfo(queryParams: UrlQueryParams): SortInfo | undefined {
        const paramValue: string | undefined = queryParams[ParamField.Sort];

        return paramValue ? parseSortInfo(paramValue) : undefined;
    }

    public static queryParamsToPage(queryParams: UrlQueryParams): number | undefined {
        const paramValue: string | undefined = queryParams[ParamField.Page];

        return paramValue ? Number.parseInt(paramValue) : undefined;
    }

    public static queryParamsToPageSize(queryParams: UrlQueryParams): number | undefined {
        const paramValue: string | undefined = queryParams[ParamField.PageSize];

        return paramValue ? Number.parseInt(paramValue) : undefined;
    }

    public static queryParamsToView(queryParams: UrlQueryParams): DisplayMode | undefined {
        const paramValue: string | undefined = queryParams[ParamField.View];

        return paramValue ? createDisplayMode(paramValue) : undefined;
    }
    // #endregion

    // #region util
    private static determineQueryPartStrategy(isExcludeOperation: boolean, isOrOperation: boolean): QueryPartStrategy {
        if (isExcludeOperation) {
            return QueryPartStrategy.MatchNone;
        }
        return isOrOperation ? QueryPartStrategy.MatchAtLeastOne : QueryPartStrategy.MatchAll;
    }

    private static determineOperand(isExcludeOperation: boolean, isOrOperation: boolean): Operand {
        if (isExcludeOperation) {
            return Operand.Exclude;
        }
        return isOrOperation ? Operand.Should : Operand.Include;
    }
    //# endregion
}
